import { TranslatePipe } from "@ngx-translate/core";
import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, resource } from "@angular/core";
import { Router } from "@angular/router";

import { PrivateQuery } from "../../apis/networkdashboard/graphql/graphql";
import { CertHeader } from "../../components/cert/cert-header/cert-header";
import { CertTabs } from "../../components/cert/cert-tabs/cert-tabs";
import {
  AIRT_SCANS_KEY,
  CBW_KEY,
  DDOS_FILTER_KEY,
  DNS_FIREWALL_KEY,
} from "../../components/cert/cert-tabs/cert-tabs.constants";
import { FooterComponent } from "../../components/common/footer/footer";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { AuthService } from "../../services/authservice";
import { IP_PREFIXES_LIST_QUERY } from "../ip-prefixes/ip-prefixes.rgql";
import { CbwTab } from "./components/cbw-tab/cbw-tab";
import { DdosTab } from "./components/ddos-tab/ddos-tab";
import { DnsFirewallTab } from "./components/dns-firewall-tab/dns-firewall-tab";
import { ScanTab } from "./components/scan-tab/scan-tab";

@Component({
  selector: "page-cert",
  templateUrl: "cert.html",
  styleUrl: "cert.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CbwTab,
    CertHeader,
    CertTabs,
    DdosTab,
    DdosTab,
    DnsFirewallTab,
    FooterComponent,
    NavigationComponent,
    ScanTab,
    TranslatePipe,
  ],
})
export class CertPage {
  private router = inject(Router);
  private apollo = inject(Apollo);
  private auth = inject(AuthService);

  // this value is read from the URL (see dashboard-routing.module.ts)
  // Setting a default string does not work, because if `:initialTab` is absent in the URL,
  // the signal defaults to undefined
  readonly initialTab = input<string | undefined>(undefined);
  protected activeTab = linkedSignal(() => {
    const init = this.initialTab()?.toLowerCase();
    return [AIRT_SCANS_KEY, CBW_KEY, DDOS_FILTER_KEY, DNS_FIREWALL_KEY].find((t) => t === init) ?? AIRT_SCANS_KEY;
  });

  protected showAirtScansTab = computed(() => this.activeTab() === AIRT_SCANS_KEY);
  protected showDdosFilterTab = computed(() => this.activeTab() === DDOS_FILTER_KEY);
  protected showDnsFirewallTab = computed(() => this.activeTab() === DNS_FIREWALL_KEY);
  protected showCbwTab = computed(() => this.activeTab() === CBW_KEY);

  private prefixResource = resource({
    params: () => ({ customer: this.auth.viewingCustomer().customerId }),
    loader: ({ params }) =>
      firstValueFrom(
        this.apollo.query<Pick<PrivateQuery, "ipPrefixList">>({
          query: IP_PREFIXES_LIST_QUERY,
          errorPolicy: "all",
          variables: { customer: params.customer },
        }),
      ),
  });

  protected prefixes = computed(() =>
    (this.prefixResource.value()?.data?.ipPrefixList?.page ?? [])
      .map((s) => s.ipPrefix?.prefix)
      .filter((p): p is string => p !== undefined),
  );

  protected changeTab(tab: string) {
    this.router.navigate(["/cert", tab], { replaceUrl: true });
    this.activeTab.set(tab);
  }
}
